import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { ArrowRight, Heart, Users, Handshake, Globe, Shield, GraduationCap } from "lucide-react";

const AMBITI = [
  { icon: Users, titolo: "Inclusione lavorativa", desc: "Programmi di inserimento e reinserimento lavorativo per persone in situazioni di fragilità, in collaborazione con enti del terzo settore e istituzioni locali." },
  { icon: Heart, titolo: "Sostegno alle comunità locali", desc: "Iniziative di solidarietà a favore delle comunità dei quartieri di Roma, con particolare attenzione alle periferie e alle aree meno servite." },
  { icon: Handshake, titolo: "Parità e non discriminazione", desc: "Confcommercio Roma ha ottenuto la Certificazione UNI/PdR 125 per la parità di genere, dimostrando un impegno concreto verso l'uguaglianza e la valorizzazione delle diversità." },
  { icon: Globe, titolo: "Sostenibilità ambientale", desc: "Progetti dedicati alla transizione verde delle imprese: dal bilancio di sostenibilità per le PMI ai percorsi ESG, fino alle iniziative ImprendiGreen." },
  { icon: Shield, titolo: "Legalità e sicurezza", desc: "Protocolli con la Prefettura per la sicurezza degli esercizi commerciali, sportelli antiusura e supporto per il Rating di Legalità." },
  { icon: GraduationCap, titolo: "Formazione e crescita", desc: "Percorsi formativi gratuiti e finanziati per imprenditori e dipendenti, con focus su innovazione, digitalizzazione e competenze trasversali." },
];

const PROGETTI = [
  {
    nome: "Seconda Chance",
    slug: "seconda-chance",
    img: "/progetto-seconda-chance.webp",
  },
  {
    nome: "Progetto Calma Sensoriale",
    slug: "progetto-calma-sensoriale",
    img: "/progetto-calma-sensoriale.webp",
  },
  {
    nome: "GenerAzione Donna",
    slug: "generazione-donna",
    img: "/progetto-generazione-donna.jpg",
  },
  {
    nome: "Green Business: Il bilancio di sostenibilità per le PMI",
    slug: "green-business",
    img: "/progetto-green-business.jpg",
  },
  {
    nome: "Patto Giubileo Per il lavoro",
    slug: "patto-giubileo-per-il-lavoro",
    img: "/progetto-patto-giubileo.webp",
  },
  {
    nome: "Orientare il lavoro alla parità di genere",
    slug: "orientare-il-lavoro-alla-parita-di-genere",
    img: "/progetto-orientare-lavoro.png",
  },
  {
    nome: "ImprendiGreen",
    slug: "imprendigreen",
    img: "/progetto-imprendigreen.png",
  },
];

export default function ImpegnoPage() {
  return (
    <main id="main-content">
      {/* Hero */}
      <section className="bg-[var(--confcom-blue)] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeroBreadcrumb />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Il nostro impegno</h1>
          <p className="text-lg text-white/80 mt-4 max-w-3xl">Fare impresa per noi significa anche contribuire a costruire una società più equa, inclusiva e sostenibile.</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-4">Confcommercio Roma non si limita a rappresentare le imprese: si impegna attivamente per il benessere del territorio e delle persone che lo abitano.</p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">Attraverso progetti concreti, collaborazioni istituzionali e iniziative dal basso, lavoriamo ogni giorno per promuovere inclusione, legalità, sostenibilità e pari opportunità. Perché un tessuto imprenditoriale sano è anche un tessuto sociale forte.</p>
        </div>
      </section>

      {/* Foto impegno */}
      <div className="bg-white pb-12 pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src="/impegno-sociale.jpg" alt="Impegno sociale Confcommercio Roma" className="w-full h-[340px] object-cover rounded-xl" />
        </div>
      </div>

      {/* Ambiti di impegno */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-10 text-center">I nostri ambiti di impegno</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AMBITI.map((a) => (
              <div key={a.titolo} className="bg-white rounded-xl border border-[var(--confcom-border)] p-6 mb-5">
                <div className="w-12 h-12 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center mb-4">
                  <a.icon size={24} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{a.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Progetti correlati */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Progetti attivi</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PROGETTI.map((p) => (
              <a
                key={p.slug}
                href={`https://www.confcommercioroma.it/progetti/${p.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:border-[var(--confcom-blue)]/30 hover:shadow-md transition-all"
              >
                <div className="h-44 overflow-hidden">
                  <img src={p.img} alt={p.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="p-5 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] group-hover:text-[var(--confcom-blue)] transition-colors leading-snug">{p.nome}</span>
                  <ArrowRight size={16} className="text-[var(--confcom-text-muted)] group-hover:text-[var(--confcom-blue)] shrink-0 transition-colors" aria-hidden="true" />
                </div>
              </a>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/progetti" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--confcom-blue)] hover:underline">
              Vedi tutti i progetti <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-2xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-4">Scopri Confcommercio Roma</h2>
          <p className="text-white/80 mb-6">Una rete di imprese che condivide valori, competenze e progetti per un terziario più forte e responsabile.</p>
          <Link to="/associati-ora" className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg">
            Associati ora <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}
